///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { GtthAbstractCommand } from '../../abstract-command';
import { ActivityDTO } from '../../../../../services/project-socket/activity-dto.model';
import { GtthRemoveLinkCommand } from '../link/remove-link-command';
import { GtthCreateLinkCommand } from '../link/create-link-command';
import { GtthLinkFeatureDtoModel } from '../../../../../services/project-socket/dto/gtth-link-feature-dto.model';
import { GtthPreloaderModalEvent } from '../../../../../shared/preloader-modal/preloader-modal-event';
import { GtthProgressWindowModel } from '../../../../project-windows/progress-window/progress-window.model';
import { GtthRemoveMultipleEntitiesCommand } from '../remove-multiple-entities-command';
import { GtthCreateMultipleEntitiesCommand } from '../create-multiple-entities-command';
import { GtthLogicalTopology } from '../../../entities/logical-topology';
import { AutoConnectDTO, AutoConnectType } from '../../../../../generated/shared-types.model';

export class GtthAutoConnectCommand extends GtthAbstractCommand {
    private linksOld: GtthLinkFeatureDtoModel[];

    getCommandName(): string {
        return 'GtthAutoConnectCommand';
    }

    static createDTO(): AutoConnectDTO {
        return {
            num_nodes: 5,
            isNumNodesChanged: false,
            pop_only: AutoConnectType.ANY_TO_ANY,
            radius: 0
        } as AutoConnectDTO;
    }

    create(): this {
        this.historyService.logicalTopology.autoConnectOptions.num_nodes =
            this.historyService.logicalTopology.getAutoConnectNumNeighbours();
        if (this.historyService.logicalTopology.activeLayerIdx >= GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX) {
            this.historyService.logicalTopology.autoConnectOptions.radius =
                this.historyService.logicalTopology.productMaxDistancePerLayer[this.historyService.logicalTopology.activeLayerIdx];
        } else {
            this.historyService.logicalTopology.autoConnectOptions.radius = Math.max(
                ...this.historyService.logicalTopology.productMaxDistancePerLayer
            );
        }

        return this;
    }

    doCommand(): void {
        this.historyService.progressWindowService.show(GtthProgressWindowModel.create(this.getCommandName()));

        const activity = this.createActivityDTO(false);
        activity.logicalTopology = this.historyService.logicalTopology.toGeoJsonMainConfig(this.historyService, this.project.name);
        this.sendActivity(activity);
    }

    doCommandActivity(activityDto: ActivityDTO): void {
        this.historyService.progressWindowService.show(GtthProgressWindowModel.createFromActivityDTO(activityDto));

        if (!activityDto.progress?.finished) {
            return;
        }

        if (!activityDto.successful) {
            if (this.commandResolve) {
                this.commandResolve(false);
            }
            return;
        }

        const removeLinksCommands = [];
        const addLinksCommands = [];

        this.linksOld = [];

        const linksIterator = this.historyService.logicalTopology.getLinksIterator();
        for (const link of linksIterator) {
            if (link.isUserForced()) {
                continue;
            }

            this.linksOld.push(link.toGeoJson());
            const removeCommand = this.historyService.command(GtthRemoveLinkCommand).createFromId(link.getId());
            removeLinksCommands.push(this.historyService.doCommandBypassHistory(removeCommand, true));
        }

        Promise.all(removeLinksCommands).then((_removeRes) => {
            activityDto.links
                .filter((link: GtthLinkFeatureDtoModel) => !link.getIsUserForced())
                .forEach((linkJson) => {
                    const createLinkCommand = this.historyService.command(GtthCreateLinkCommand).createFromGeoJson(linkJson);
                    addLinksCommands.push(this.historyService.doCommandBypassHistory(createLinkCommand, true));
                });

            Promise.all(addLinksCommands).then((_createRes) => {
                this.historyService.bomReportService.clearBom();
                this.historyService.logicalTopology.clearNodeNamesSectorsToShow();
                this.historyService.logicalTopology.clearDesignIterationInfo();

                if (this.commandResolve) {
                    this.historyService.logicalTopology.setShowPlanningMode(this.historyService, false, true).then((result: boolean) => {
                        this.commandResolve(result);
                    });
                }
            });
        });
    }

    undoCommand(): void {
        this.historyService.eventManager.broadcast({
            name: 'togglePreloaderModal',
            eventObject: GtthPreloaderModalEvent.show('Loading')
        });

        const currentLinks = [];
        const linksIterator = this.historyService.logicalTopology.getLinksIterator();
        for (const link of linksIterator) {
            currentLinks.push(link);
        }

        const removeMultipleLinksCommand = this.historyService
            .command(GtthRemoveMultipleEntitiesCommand)
            .createFromObjects([], currentLinks);

        this.historyService.doCommandBypassHistory(removeMultipleLinksCommand).then(() => {
            const linksToCreate = this.linksOld.filter((link) => {
                return !link.getIsUserForced();
            });

            const createLinksCommand = this.historyService.command(GtthCreateMultipleEntitiesCommand).create([], linksToCreate);

            this.historyService.doCommandBypassHistory(createLinksCommand).then(() => {
                this.historyService.eventManager.broadcast({
                    name: 'togglePreloaderModal',
                    eventObject: GtthPreloaderModalEvent.hide()
                });

                this.commandResolve(false);
            });
        });
    }

    isDisabledInReadOnlyMode(): boolean {
        return true;
    }

    isModifyTopology(): boolean {
        return true;
    }

    isClearDesign(): boolean {
        return false;
    }
}
